// utilities/ticketTemplateText.js
//
// PURE mirror of sound-api/utilities/ticketTemplateText.mjs (SOUND-544). A
// workflow template's `titleTemplate` / `descriptionTemplate` prefill the
// create-ticket modal; the API validates at SAVE. Keep the grammar, allowlist
// and render rules byte-for-byte in step with the API file — a drift here means
// the editor hint, the inline warning and the prefill all disagree with what
// the server accepts.
//
// Placeholder grammar: `{{name}}`, optional inner whitespace (`{{ client }}`).
// Allowlist: {{client}}, {{requestType}}, {{variant}}, {{<intake field key>}}.
// A reserved name wins over an intake field that happens to share it.

export const RESERVED_TEMPLATE_PLACEHOLDERS = ["client", "requestType", "variant"];

export const TEMPLATE_TEXT_MAX = { titleTemplate: 300, descriptionTemplate: 5000 };

const PLACEHOLDER_RE = /\{\{(.*?)\}\}/g;
const NAME_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

/** Every `{{…}}` occurrence in order, trimmed (malformed names as written). */
export function extractPlaceholders(text) {
  if (typeof text !== "string") return [];
  return Array.from(text.matchAll(PLACEHOLDER_RE), (m) => m[1].trim());
}

/**
 * Validate one template string against the allowlist. Same messages as the API.
 * @returns {string[]} errors (empty = valid)
 */
export function validateTemplateText(text, { intakeFieldKeys = [], field = "template" } = {}) {
  if (text == null) return [];
  if (typeof text !== "string") return [`${field} must be a string or null`];
  const errors = [];
  const max = TEMPLATE_TEXT_MAX[field];
  if (max && text.length > max) errors.push(`${field} must be at most ${max} characters`);

  const allowed = new Set([...RESERVED_TEMPLATE_PLACEHOLDERS, ...intakeFieldKeys]);
  const reported = new Set();
  for (const name of extractPlaceholders(text)) {
    if (reported.has(name)) continue;
    if (!NAME_RE.test(name)) {
      errors.push(`${field} has a malformed placeholder "{{${name}}}"`);
      reported.add(name);
    } else if (!allowed.has(name)) {
      errors.push(
        `${field} has an unknown placeholder "{{${name}}}" — allowed: ` +
          `{{client}}, {{requestType}}, {{variant}} or an intake field key of this template`
      );
      reported.add(name);
    }
  }
  return errors;
}

function formatValue(v) {
  if (v == null) return "";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.map(formatValue).filter(Boolean).join(", ");
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === "object") return "";
  return String(v);
}

/**
 * Render a template string. Unknown or unanswered placeholders render as "".
 * @returns {string|null} null when there is no template
 */
export function renderTemplateText(text, { client, requestType, variant, intake = {} } = {}) {
  if (typeof text !== "string") return null;
  const reserved = {
    client,
    requestType,
    variant: variant ?? requestType,
  };
  return text.replace(PLACEHOLDER_RE, (_m, raw) => {
    const name = raw.trim();
    if (Object.prototype.hasOwnProperty.call(reserved, name)) return formatValue(reserved[name]);
    if (intake && Object.prototype.hasOwnProperty.call(intake, name)) return formatValue(intake[name]);
    return "";
  });
}

// ── frontend-only helpers (not in the API file) ─────────────────────────────

const REF_FIELD_TYPES = ["mid-ref", "company-ref"];

/**
 * The intake map a prefill renders against. Only VISIBLE fields count (a field
 * hidden by its predicate is not sent, so it must not leak into the title), and
 * a reference field renders its human LABEL, not the UUID it stores.
 *
 * @param {Array<{key: string, type?: string, visibleWhen?: Object}>} fields
 * @param {Object} values - intake answers keyed by field key
 * @param {Object} labels - display labels for ref fields, keyed by field key
 * @param {(predicate: Object, values: Object) => boolean} isVisible
 */
export function buildRenderIntake(fields = [], values = {}, labels = {}, isVisible = () => true) {
  const out = {};
  (fields || []).forEach((f) => {
    if (!f?.key || !isVisible(f.visibleWhen, values)) return;
    const v = values[f.key];
    if (v === undefined) return;
    out[f.key] = REF_FIELD_TYPES.includes(f.type) && labels[f.key] ? labels[f.key] : v;
  });
  return out;
}
